import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, EnvelopeSimple, Key, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(token ? 'reset' : 'request');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const requestReset = async () => {
    if (!email) return toast.error('Enter your email');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setDone(true);
      toast.success('Check your email for a reset link');
    } catch (err) {
      toast.error('Something went wrong');
    } finally { setLoading(false); }
  };

  const confirmReset = async () => {
    if (newPassword.length < 6) return toast.error('Password must be 6+ characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword });
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="app-container flex flex-col items-center justify-center min-h-screen px-6" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <button onClick={() => navigate('/login')} className="mb-6 flex items-center gap-2 text-[#71717A] text-sm">
          <ArrowLeft size={16} /> Back to Login
        </button>

        <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          {step === 'request' ? 'Forgot Password?' : 'Set New Password'}
        </h1>
        <p className="text-[#71717A] text-sm mb-6">
          {step === 'request' ? "Enter your email and we'll send a reset link." : 'Enter your new password below.'}
        </p>

        {step === 'request' && !done && (
          <>
            <div className="relative mb-4">
              <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" className="input-field pl-10" data-testid="reset-email-input" />
            </div>
            <button onClick={requestReset} disabled={loading} className="btn-primary w-full" data-testid="send-reset-btn">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </>
        )}

        {step === 'request' && done && (
          <div className="p-4 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-center">
            <Check size={32} weight="bold" className="text-[#39FF14] mx-auto mb-2" />
            <p className="text-white text-sm">Reset link sent! Check your inbox.</p>
          </div>
        )}

        {step === 'reset' && (
          <>
            <div className="relative mb-3">
              <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" className="input-field pl-10" data-testid="new-password-input" />
            </div>
            <div className="relative mb-4">
              <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="input-field pl-10" data-testid="confirm-password-input" />
            </div>
            <button onClick={confirmReset} disabled={loading} className="btn-primary w-full" data-testid="confirm-reset-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
